import { Component, OnInit, OnDestroy, ViewChild, ViewChildren, Inject } from '@angular/core'
import { Router, ActivatedRoute } from '@angular/router'
import { Location, DatePipe } from '@angular/common'

import { Subscription } from "rxjs"
import { TimerObservable } from "rxjs/observable/TimerObservable"
import { BaseChartDirective } from 'ng2-charts/ng2-charts'
import { TranslateService, LangChangeEvent } from '@ngx-translate/core'

import { AwsService } from '../../common/aws.service'
import { Chart } from './chart.model'
import { TimeRangeOption } from './time-range-option.model'
import { ThingService } from '../thing.service'
import { MessageService } from '../../messages/message.service'

@Component({
    selector: 'app-details',
    templateUrl: './details.component.html',
    styleUrls: ['./details.component.css']
})
export class DetailsComponent implements OnInit, OnDestroy {
    @ViewChild(BaseChartDirective) chartDirective: BaseChartDirective
    @ViewChild('thingGraphTop') thingGraphTop: any
    @ViewChild('thingSensors') thingSensors: any

    // Listen route change
    private routeSubscription: Subscription
    // Listen for thing update events
    private thingSubscription: Subscription
    // Listen for language change
    private translateSubscription: Subscription

    // Selections made with url path (ngOnInit)
    public selectedThing: any
    public timeRange: TimeRangeOption

    // Chart height is controlled with some custom code
    public chartHeight: number = 400

    // Chart view and scroll
    public liveMode: boolean
    public startMS: number
    public endMS: number
    // Date picker
    public datePickerVisible: boolean = false
    public datePickerDate: Date = new Date()
    public datePickerMaxDate: Date = new Date()

    // Spinner indicator
    public loading: boolean = false
    // Date shown above graphs
    public currentDate: Date = new Date()
    // Time range options visible in UI, defines button label, url path parameter and time range
    public timeRangeOptions: TimeRangeOption[] = [
        new TimeRangeOption('details.10_min', '10min', 10),
        new TimeRangeOption('details.hour', 'hour', 60),
        new TimeRangeOption('details.day', 'day', 60 * 24)
    ]

    // Master data for charts
    private historyData: any = {}
    // Chart options (verbose) are hidden inside a class
    public chart: Chart = new Chart()
    // Translated labels
    private labelTranslations: any = {}

    constructor(
        private awsService: AwsService,
        private thingService: ThingService,
        private router: Router,
        private route: ActivatedRoute,
        private location: Location,
        private translateService: TranslateService,
        private messageService: MessageService,
        private datePipe: DatePipe,
        @Inject('Window') private window: Window) { }

    ngOnInit() {
        console.log('details.ngOnInit things', this.thingService.things)

        this.routeSubscription = this.route.params.subscribe(params => {
            console.log('Route param subscription triggered', params)
            this.selectedThing = this.thingService.getThingByName(params['thing'])

            let timeRange: string = params['timeRange']
            let matches: TimeRangeOption[] = this.timeRangeOptions.filter(option => option.param === timeRange)
            if (matches && matches.length >= 1) {
                this.timeRange = matches[0]
            } else {
                this.timeRange = this.timeRangeOptions[1]
            }

            let time: string = params['time']
            let now: number = new Date().getTime()
            this.endMS = time ? Number(time) : now
            if (this.endMS > now) {
                this.endMS = now
            }
            this.startMS = this.endMS - (this.timeRange.range * 60 * 1000)
            this.datePickerDate = new Date(this.endMS)
            this.liveMode = (time === undefined) ? true : false
            this.getHistoryData(this.selectedThing)
        })

        this.thingSubscription = this.thingService.thingsObservable.subscribe(thing => {
            if (thing.name === this.selectedThing.name) {
                // Update thing state visible below the graphs
                this.selectedThing.stateObject = thing.stateObject
                // Update charts with latest value incrementally
                this.updateChartSingleValue(thing.stateObject)
            }
        })

        this.loadTranslations()

        // If language is changed, the chart labels must be manually updated
        this.translateSubscription = this.translateService.onLangChange.subscribe((event: any) => {
            this.labelTranslations['details.temperature'] = event.translations['details']['temperature']
            this.labelTranslations['details.humidity'] = event.translations['details']['humidity']
            this.labelTranslations['details.index'] = event.translations['details']['index']
            this.labelTranslations['details.noise_level'] = event.translations['details']['noise_level']
            this.labelTranslations['details.roof_temperature'] = event.translations['details']['roof_temperature']
            this.labelTranslations['details.co2'] = event.translations['details']['co2']
            // Labels in chart need to manually updated
            this.updateChart()
        })

        this.updateChartHeight(false)
    }

    ngOnDestroy() {
        if (this.routeSubscription) {
            this.routeSubscription.unsubscribe()
        }
        if (this.thingSubscription) {
            this.thingSubscription.unsubscribe()
        }
        if (this.translateSubscription) {
            this.translateSubscription.unsubscribe()
        }
    }

    public toggleDatePicker(): void {
        this.datePickerVisible = !this.datePickerVisible
    }

    private loadTranslations(): void {
        let promises: Promise<any>[] = []
        promises.push(this.translate('details.temperature'));
        promises.push(this.translate('details.humidity'));
        promises.push(this.translate('details.index'));
        promises.push(this.translate('details.noise_level'));
        promises.push(this.translate('details.roof_temperature'));
        promises.push(this.translate('details.co2'));
        Promise.all(promises).then((result) => {
            result.forEach((item) => {
                this.labelTranslations[item.key] = item.value
            })
        })
    }

    private translate(key: string, value?: any): Promise<any> {
        return new Promise((resolve, reject) => {
            // TODO will these subscribe's leak memory because we never unsubscribe
            this.translateService.get(key, { value: value }).subscribe((result: string) => {
                resolve({ key: key, value: result })
            })
        })
    }

    public onResize(event): void {
        this.updateChartHeight(false)
    }

    private updateChartHeight(resize: boolean) {
        // TODO mobiililla menee w+h arvot miten sattuu kun kääntelee laitetta (chrome dev tools ongelma vai myös laitteella?)
        var height = Math.min(this.window.innerHeight, this.window.screen.height)
        if (resize) {
            height = Math.min(this.window.innerHeight, this.window.screen.width)
        }
        // console.log('window [w:' + this.window.innerWidth + ' h:' + this.window.innerHeight + '], screen [w:' + this.window.screen.width + ' h:' + this.window.screen.height + '] => ' + height)
        var topHeight = Math.max(38, this.thingGraphTop.nativeElement.clientHeight)
        var sensorsHeight = Math.max(79, this.thingSensors.nativeElement.clientHeight)
        // console.log('top height: ' + topHeight + ', sensors height: ' + sensorsHeight)
        this.chartHeight = Math.max((height - topHeight - sensorsHeight - 100), 200)
    }

    public listThings(): any[] {
        return this.thingService.things
    }

    private getHistoryData(thing: any): void {
        if (!thing) {
            return
        }
        this.loading = true

        this.awsService.getApiClient()
            .then((apiClient) => {
                let params = {
                    headers: {
                        'Content-type': 'application/json'
                    }
                }
                let now = new Date().getTime()
                if (this.endMS > now) {
                    console.log('endMS > now -> fix start - end values')
                    this.startMS = now - this.timeRange.range * 60 * 1000
                    this.endMS = now
                }
                let body = {
                    device_id: thing.name,
                    start_time: this.startMS,
                    end_time: this.endMS
                }
                let additionalParams = {}
                return apiClient.historyPost(params, body, additionalParams)
            })
            .then((result) => {
                if (result.data && result.data.errorMessage) {
                    console.log('details.getHistoryData ERROR', result)
                    this.loading = false
                    return
                }
                this.historyData = result.data ? result.data : []
                this.updateChart()
                this.loading = false
            })
            .catch((error) => {
                console.log('details.getHistoryData API ERROR', error)
                this.loading = false
                this.messageService.warning('details.failed_to_load_history_data')
            })
    }

    // Format date labels to chart x-axis
    private convertToLabel(date: Date): string {
        if (!date) {
            return ''
        }
        if (this.timeRange.range >= (60 * 24 * 7)) {
            return date.getDate() + '/' + (date.getMonth() + 1) + ' ' +
                ('0' + date.getHours()).slice(-2) + ':' + ('0' + date.getMinutes()).slice(-2)
        } else {
            return ('0' + date.getHours()).slice(-2) + ':' + ('0' + date.getMinutes()).slice(-2)
        }
    }

    public chartClicked(e: any): void {
        let element = this.chartDirective.chart.getElementsAtEvent(e.event)[0]
        if (element && this.historyData && this.historyData.items) {
            let keys = Object.keys(this.historyData.items)
            let dataElement = this.historyData.items[keys[element._index]]
            let clickedTimestamp = dataElement.ts

            let selectTimeRangeOption = this.timeRangeOptions[0] // 10min
            this.focusHistoryTo(clickedTimestamp, selectTimeRangeOption)
        }
    }

    public changeTimeRange(timeRangeOption: TimeRangeOption): void {
        if (this.liveMode) {
            this.router.navigate(['/details', this.selectedThing.name, timeRangeOption.param], { replaceUrl: true })
        } else {
            this.focusHistoryTo(this.endMS, timeRangeOption)
        }
    }

    public onDateChanged(e: any): void {
        let selectedDate = e
        let now = new Date()
        if (selectedDate.getTime() > now.getTime()) {
            selectedDate = now
        }

        // Keep current time range selection
        this.focusHistoryTo(selectedDate.getTime(), this.timeRange)
    }

    private focusHistoryTo(time: number, timeRange: TimeRangeOption): void {
        let params: any = {
            replaceUrl: true
        }
        this.router.navigate(['/details', this.selectedThing.name, timeRange.param, time], params)
    }

    public chartHovered(e: any): void {
        // console.log(e)
    }

    public chartHasData(): boolean {
        return this.chart
            && this.chart.lineChartData.length > 0
            && this.chart.lineChartData[0]
            && this.chart.lineChartData[0].data.length > 1
    }

    private updateChart(): void {
        let temperature: Array<number> = new Array()
        let humidity: Array<number> = new Array()
        let saunaIndex: Array<number> = new Array()
        let noiseLevel: Array<number> = new Array()
        let roofTemperature: Array<number> = new Array()
        let co2: Array<number> = new Array()
        let labels: Array<any> = new Array()

        let keys = Object.keys(this.historyData.items)
        let max: number = undefined
        keys.forEach((key) => {
            let entry: any = this.historyData.items[key]
            let t1: number = this.toFixedNumber(entry.t1)
            let h1: number = this.toFixedNumber(entry.h1)
            let m1: number = this.toFixedNumber(entry.m1)
            let t2: number = this.toFixedNumber(entry.t2)
            let c1: number = this.toFixedNumber(entry.c1)
            temperature.push(t1)
            humidity.push(h1)
            saunaIndex.push(this.toFixedNumber((t1 + h1)))
            noiseLevel.push(m1)
            roofTemperature.push(t2)
            co2.push(c1)
            labels.push(this.convertToLabel(new Date(Number(entry.ts))))
            let itemMax: number = Math.max(t1, h1, (t1 + h1), m1)
            if (max === undefined || itemMax > max) {
                max = itemMax
            }
        })

        let chartData = new Array()
        chartData.push({ yAxisID: 'A', sensor: 'm1', data: noiseLevel, label: this.labelTranslations['details.noise_level'], borderWidth: 1, lineTension: 0, pointRadius: 4, pointHoverRadius: 4 })
        chartData.push({ yAxisID: 'A', sensor: 'h1', data: humidity, label: this.labelTranslations['details.humidity'], borderWidth: 1, lineTension: 0, pointRadius: 4, pointHoverRadius: 4 })
        chartData.push({ yAxisID: 'A', sensor: 't1', data: temperature, label: this.labelTranslations['details.temperature'], borderWidth: 1, lineTension: 0, pointRadius: 4, pointHoverRadius: 4 })
        chartData.push({ yAxisID: 'A', sensor: 'ix', data: saunaIndex, label: this.labelTranslations['details.index'], borderWidth: 1, lineTension: 0, pointRadius: 4, pointHoverRadius: 4 })
        chartData.push({ yAxisID: 'A', sensor: 't2', data: roofTemperature, label: this.labelTranslations['details.roof_temperature'], borderWidth: 1, lineTension: 0, pointRadius: 4, pointHoverRadius: 4, hidden: true, hiddenLegend: true })
        chartData.push({ yAxisID: 'A', sensor: 'c1', data: co2, label: this.labelTranslations['details.co2'], borderWidth: 1, lineTension: 0, pointRadius: 4, pointHoverRadius: 4, hidden: true, hiddenLegend: true })
        chartData.push({ yAxisID: 'B', data: max ? [max] : [], label: '' }) // hack to get y-axis values to right side of chart with correct values

        // Update to UI
        this.chart.lineChartLabels = labels
        this.chart.lineChartData = chartData

        // Hack to fix labels not updating
        if (this.chartDirective && this.chartDirective.chart) {
            this.updateChartScale(max)
        } else {
            // If chart directive is undefined, try to run update after timeout
            setTimeout(() => {
                this.updateChartScale(max)
            }, 500)
        }

        this.updateChartHeight(false)
    }

    private updateChartScale(max: number): void {
        if (this.chartDirective && this.chartDirective.chart) {
            this.chartDirective.chart.config.data.labels = this.chart.lineChartLabels
            if (max > this.chartDirective.chart.config.options.scales.yAxes[0].ticks.max) {
                let ceilMax = (Math.floor(max / 20) + 1) * 20
                this.chartDirective.chart.config.options.scales.yAxes[0].ticks.max = ceilMax
                this.chartDirective.chart.config.options.scales.yAxes[1].ticks.max = ceilMax
            }
            this.chartDirective.chart.update()
        }
    }

    private toFixedNumber(value: any): number {
        return Number(Number(value).toFixed(2))
    }

    private updateChartSingleValue(stateObject: any): void {
        if (!this.historyData || !this.historyData.config) {
            // Initial history update may not be ready when this is called
            return
        }

        let key = stateObject.timestamp
        let block: any = {}
        block.t1 = Number(stateObject.t1)
        block.h1 = Number(stateObject.h1)
        block.m1 = Number(stateObject.m1)
        block.t2 = Number(stateObject.t2)
        block.c1 = Number(stateObject.m1)
        block.ts = Number(stateObject.timestamp)
        this.historyData.items[key] = block

        // Only update realtime values in case we have not scrolled away from current time view
        if (this.liveMode) {
            let now = new Date().getTime()
            this.startMS = now - this.timeRange.range * 1000 * 60
            this.endMS = now
            this.removeOldData()
            this.updateChart()
        }
    }

    private maxValue(currentValue: number, newValue: number): number {
        if (currentValue === undefined && newValue === undefined) {
            return 0
        }
        if (currentValue === undefined) {
            return newValue
        }
        return Math.max(currentValue, newValue)
    }

    private removeOldData(): void {
        if (!this.historyData) {
            return
        }
        let minTime = new Date().getTime() - (this.timeRange.range * 60 * 1000)
        Object.keys(this.historyData.items).forEach((key) => {
            if (Number(key) < minTime) {
                delete this.historyData.items[key]
            }
        })
    }

    // Convert to number for UI
    public toNumber(input): number {
        return Number(input)
    }

    public scrollBack(): void {
        let scrollAmount: number = (this.timeRange.range * 60 * 1000 / 2)
        let scrollTime = this.endMS - scrollAmount
        this.focusHistoryTo(scrollTime, this.timeRange)
    }

    public scrollForward(): void {
        let now: number = new Date().getTime()
        let scrollAmount: number = (this.timeRange.range * 60 * 1000 / 2)
        let scrollTime = this.endMS + scrollAmount

        if (scrollTime > now) {
            scrollTime = now
        }
        this.focusHistoryTo(scrollTime, this.timeRange)
    }

    public scrollNow(): void {
        let params: any = {
            replaceUrl: true
        }
        this.router.navigate(['/details', this.selectedThing.name, this.timeRange.param], params)
    }

    public scrollNowEnabled(): boolean {
        return !this.liveMode
    }

    public scrollForwardEnabled(): boolean {
        return !this.liveMode
    }

    public viewRange(): string {
        let startDate = new Date(this.startMS)
        let endDate = new Date(this.endMS)
        if (startDate.getFullYear() === endDate.getFullYear()
            && startDate.getMonth() === endDate.getMonth()
            && startDate.getDay() === endDate.getDay()) {
            return this.datePipe.transform(startDate, 'dd.MM.yyyy') + ' ' +
                this.datePipe.transform(startDate, 'HH:mm') + ' - ' + this.datePipe.transform(endDate, 'HH:mm')
        }
        return this.datePipe.transform(startDate, 'dd.MM.yyyy HH:mm') + '-' + this.datePipe.transform(endDate, 'dd.MM.yyyy HH:mm');
    }

    public sensorValue(sensor: string): any {
        if (this.liveMode) {
            if (sensor === 'ix') {
                let h1 = this.selectedThing.stateObject['h1']
                let t1 = this.selectedThing.stateObject['t1']
                if (h1 === undefined || t1 === undefined) {
                    return '-'
                }
                return (Number(h1) + Number(t1)).toFixed(0)
            }
            return this.formatSensorValue(this.selectedThing.stateObject[sensor])
        } else {
            if (this.chart.lineChartData && this.chart.lineChartData.length > 0) {
                let sensorData = this.chart.lineChartData.find((data) => { return data.sensor === sensor })
                if (sensorData && sensorData.data && sensorData.data.length > 0) {
                    return this.formatSensorValue(sensorData.data[sensorData.data.length - 1])
                }
            }
            return '-'
        }
    }

    private formatSensorValue(value: any): any {
        if (value === undefined) {
            return '-'
        }
        return value
    }
}



// WEBPACK FOOTER //
// /Users/mjahoo/workspace/rento/git/saunasampo-monitor/src/app/device/details/details.component.ts